import { Button } from '@/components/ui/button';
import { CheckCircle, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogFooter,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import LoadingCircle from '@/components/LoadingCircle';
import { useState } from 'react';
import { useProfileContext } from '@/hooks/useProfileContext';
import { GiftRequestStatus } from '@/api/gifts-requests';
import {
  GiftRequestDTO,
  useApproveGiftRequest,
} from '@/hooks/useGiftsRequests';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  orderRejectSchema,
  OrderRejectFormValues,
} from '@/schemas/order-reject';
import { FormControl } from '@/components/ui/form';
import { FormMessage } from '@/components/ui/form';
import { FormItem } from '@/components/ui/form';
import { FormField } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface ApproveProps {
  giftRequest: GiftRequestDTO;
  text: string;
}

const ApproveButton = ({ giftRequest, text }: ApproveProps) => {
  const [isApproveOrReject, setIsApproveOrReject] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const changeStatusGiftRequest = useApproveGiftRequest();
  const { profile } = useProfileContext();

  const form = useForm<OrderRejectFormValues>({
    resolver: zodResolver(orderRejectSchema),
    defaultValues: {
      reasonReject: '',
    },
  });

  const checkStatusSend = (isApprove: string) => {
    switch (profile?.role?.key) {
      case 'admin':
        return isApprove === 'approve'
          ? GiftRequestStatus.ADMIN_APPROVED
          : GiftRequestStatus.ADMIN_REJECTED;
      case 'general-director':
        return isApprove === 'approve'
          ? GiftRequestStatus.GENERAL_DIRECTOR_APPROVED
          : GiftRequestStatus.GENERAL_DIRECTOR_REJECTED;
      default:
        return isApprove === 'approve'
          ? GiftRequestStatus.APPROVED
          : GiftRequestStatus.REJECTED;
    }
  };

  const handleApproveOrReject = async (
    isApprove: string,
    reasonReject?: string
  ) => {
    setIsApproveOrReject(isApprove);
    const payload = {
      id: giftRequest.id,
      status: checkStatusSend(isApprove),
      reasonReject,
    };
    await changeStatusGiftRequest.mutateAsync(payload);
    setIsApproveOrReject('');
    setIsOpen(false);
  };

  const onSubmit = (values: OrderRejectFormValues) => {
    handleApproveOrReject('reject', values.reasonReject);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        {text === 'phê duyệt' ? (
          <Button
            variant="destructive"
            className="bg-green-500 hover:bg-green-600 border-green-600 transition-colors capitalize"
          >
            <CheckCircle className="w-4 h-4" />
            {text}
          </Button>
        ) : (
          <Button
            variant="destructive"
            className="bg-red-500 hover:bg-red-600 border-red-600 transition-colors capitalize"
          >
            <X className="w-4 h-4" />
            {text}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="luxury-dialog">
        <AlertDialogDescription className="sr-only">
          Xác nhận duyệt yêu cầu tặng quà
        </AlertDialogDescription>
        <AlertDialogHeader className="luxury-header relative">
          <div className="absolute -top-2 -right-2 cursor-pointer bg-[unset] border-none hover:bg-transparent shadow-none">
            <X
              onClick={() => {
                setIsOpen(false);
                form.reset();
              }}
              className="w-4 h-4"
            />
          </div>
          <AlertDialogTitle className="luxury-title">
            Xác nhận {text} yêu cầu tặng quà
          </AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-6">
          <div className="luxury-card">
            <p className="text-center text-lg">
              Bạn có chắc chắn muốn {text} yêu cầu tặng quà ADYC -{' '}
              {giftRequest.id}?
            </p>
          </div>
        </div>

        {(giftRequest.reasonReject || giftRequest.explanation) && (
          <div className="luxury-card">
            <div className="text-base font-medium  whitespace-pre-line break-words w-full mt-1">
              Lý do từ chối gần nhất: {giftRequest.reasonReject}
            </div>
            <div className="text-base font-medium  whitespace-pre-line break-words w-full mt-1">
              Lý do muốn được phê duyệt lại: {giftRequest.explanation}
            </div>
          </div>
        )}

        {text === 'phê duyệt' ? (
          <AlertDialogFooter className="mt-6 space-x-4">
            <Button
              onClick={() => setIsOpen(false)}
              className="w-[120px] bg-red-500 hover:bg-red-600 text-white transition-colors"
            >
              Huỷ bỏ
            </Button>
            <Button
              onClick={() => handleApproveOrReject('approve')}
              className="w-[120px] bg-green-500 hover:bg-green-600 text-white transition-colors"
              disabled={changeStatusGiftRequest.isPending}
            >
              {changeStatusGiftRequest.isPending &&
              isApproveOrReject === 'approve' ? (
                <LoadingCircle />
              ) : (
                'Xác nhận'
              )}
            </Button>
          </AlertDialogFooter>
        ) : (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormField
                control={form.control}
                name="reasonReject"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Label required className="luxury-label">
                      Lý do từ chối
                    </Label>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="min-h-[100px] luxury-input bg-white"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-center space-x-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    form.reset();
                  }}
                  className="w-[120px] bg-red-500 hover:bg-red-600 text-white transition-colors"
                >
                  Huỷ bỏ
                </Button>
                <Button
                  type="submit"
                  className="w-[120px] bg-green-500 hover:bg-green-600 text-white transition-colors"
                  disabled={changeStatusGiftRequest.isPending}
                >
                  {changeStatusGiftRequest.isPending &&
                  isApproveOrReject === 'reject' ? (
                    <LoadingCircle />
                  ) : (
                    'Xác nhận'
                  )}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ApproveButton;
